// =============================================================================
// Priority Spel V3 — game store (Pinia)
// Holds the server-authoritative `state` (from apiClient) plus UI-local bits.
// Views read via getters; actions call the client and refresh `state`.
// =============================================================================
import { defineStore } from 'pinia'
import { client } from '@/services/apiClient'
import { MINIGAMES, TIERS } from '@/config/gameConfig'

export const useGameStore = defineStore('game', {
  state: () => ({
    uuid: null,
    isAuthenticated: false,
    server: null, // the backend state snapshot
    loading: false,
    error: null,
    // UI-local
    lastSpinOutcome: null, // {type,gameId?,amount?} from the most recent spin
    lastStarsEarned: 0, // for the score / star animation
    // Stars earned while the top bar was NOT on screen (i.e. during a minigame),
    // so it can count them up one by one when the player lands back on home.
    // Set by finishGame, cleared by the counter once it has played them.
    starsToCelebrate: 0,
    lastPoints: 0, // raw game score for the score screen "N Punten" (Figma 393:428)
    shopDraft: {}, // { [prizeId]: stars staged before confirm }
    prizes: [], // cached prize catalogue (from loadShop)
  }),

  getters: {
    player: (s) => s.server?.player ?? null,
    cycle: (s) => s.server?.cycle ?? null,
    week: (s) => s.server?.week ?? null,
    activeGame: (s) => s.server?.activeGame ?? null,

    starBalance: (s) => s.server?.cycle?.starBalance ?? 0,
    weekNumber: (s) => s.server?.cycle?.week ?? 1,
    weeksTotal: (s) => s.server?.cycle?.weeksTotal ?? 7,
    // How many weeks of this cycle the player actually played in (§14) — NOT
    // cycle.week, which just counts the weeks that have passed. `null` when the
    // backend does not report it, so the eindscherm can say so instead of
    // showing the round number under a "meegespeeld" label.
    weeksPlayed: (s) => s.server?.cycle?.weeksPlayed ?? null,
    turnsRemaining: (s) => s.server?.week?.turnsRemaining ?? 0,
    turnsTotal: (s) => s.server?.week?.turnsTotal ?? 0,
    hasTurns: (s) => (s.server?.week?.turnsRemaining ?? 0) > 0,
    seenGlobalIntro: (s) => !!s.server?.seenGlobalIntro,

    tierLabel: (s) => {
      const t = s.server?.player?.tier
      return (t && TIERS[t]?.label) || ''
    },

    // Minigame metadata for the active/last game
    activeGameMeta: (s) => {
      const id = s.server?.activeGame?.gameId
      return id ? MINIGAMES.find((g) => g.id === id) : null
    },

    // Which post-shop steps to show, in order (§13). Empty → straight to eindscherm.
    pendingOptinSteps: (s) => {
      const p = s.server?.player
      if (!p) return []
      const steps = []
      if (!p.optinCommunication) steps.push('communication') // §13.1 repeats until accepted
      if (!p.optinReminderAsked) steps.push('reminder') // §13.2 once per player
      if (!p.emailValidated) steps.push('email') // §13.3 once per player
      return steps
    },
  },

  actions: {
    _apply(res) {
      if (res?.state) this.server = res.state
      return res
    },

    async authenticate(payload = {}) {
      this.loading = true
      this.error = null
      try {
        const res = await client.authenticate(payload)
        this.uuid = res.uuid
        this.isAuthenticated = true
        this._apply(res)
        return res
      } catch (e) {
        this.error = e?.message || 'auth_failed'
        throw e
      } finally {
        this.loading = false
      }
    },

    async refresh() {
      return this._apply(await client.getState())
    },

    async markIntroSeen() {
      return this._apply(await client.markIntroSeen())
    },

    async spin() {
      const res = await client.spin()
      if (res.outcome) this.lastSpinOutcome = res.outcome
      return this._apply(res)
    },

    async finishGame(result) {
      const res = await client.finishGame(result)
      this.lastStarsEarned = res.starsEarned ?? 0
      this.starsToCelebrate = this.lastStarsEarned
      this.lastPoints = res.points ?? result?.score ?? 0
      return this._apply(res)
    },

    // The top bar has finished counting the earned stars up — do not replay them.
    celebrationShown() {
      this.starsToCelebrate = 0
    },

    async quitGame(payload) {
      const res = await client.quitGame(payload)
      this.lastStarsEarned = 0
      return this._apply(res)
    },

    async loadShop() {
      const res = await client.getShop()
      if (res.prizes) this.prizes = res.prizes
      this._apply(res)
      return res
    },

    async confirmShop(bets) {
      const res = await client.confirmShop({ bets })
      this._apply(res)
      if (!res.error) this.shopDraft = {}
      return res
    },

    // opt-ins / email
    // The server refuses too many code requests with HTTP 429 (+ Retry-After). Turn that into the
    // same { ok: false, error: 'too_many_requests', retryAfter } result the mock returns, so the
    // view handles one shape for both backends.
    async requestMsisdnCode(msisdn) {
      try {
        return this._apply(await client.requestMsisdnCode({ msisdn }))
      } catch (e) {
        if (e?.status !== 429) throw e
        return this._apply({
          ok: false,
          error: 'too_many_requests',
          retryAfter: e.data?.retryAfter ?? null,
          state: e.data?.state,
        })
      }
    },
    verifyMsisdn(code) {
      return client.verifyMsisdn({ code }).then((r) => this._apply(r))
    },
    setCommunicationOptin(accept) {
      return client.setCommunicationOptin({ accept }).then((r) => this._apply(r))
    },
    setReminderOptin(accept) {
      return client.setReminderOptin({ accept }).then((r) => this._apply(r))
    },
    validateEmail(email) {
      return client.validateEmail({ email }).then((r) => this._apply(r))
    },

    // dev helpers (simulate §16 resets)
    devWeeklyReset() {
      return client._devWeeklyReset().then((r) => this._apply(r))
    },
    devCycleReset() {
      return client._devCycleReset().then((r) => this._apply(r))
    },
    devReset() {
      return client._devReset().then((r) => this._apply(r))
    },
  },
})
